<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import Icon from 'next/icon/Icon.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { accountCurrency } from 'dashboard/helper/currency';
import { groupPricingRows, pricingLineKey } from '../pricingLineKey';
import { computeSuggestedPricing } from '../suggestedPricing';
import { pricingUnitShortLabel } from 'dashboard/helper/pricingUnitLabel';

const props = defineProps({
  selectedPlan: { type: Object, default: null },
  plans: { type: Array, default: () => [] },
  selectedPlanId: { type: [String, Number], default: null },
  activeBreakdown: { type: Object, default: null },
  compareQuote: { type: Object, default: null },
  quotes: { type: Array, default: () => [] },
  planLocked: { type: Boolean, default: false },
  quotesEnabled: { type: Boolean, default: false },
  pricingLocked: { type: Boolean, default: false },
  lockingPrice: { type: Boolean, default: false },
  formattedTotal: { type: String, default: null },
  formattedImpact: { type: String, default: null },
  formattedDelta: { type: String, default: null },
  impactDelta: { type: Number, default: null },
  priceDelta: { type: Number, default: null },
  loadingPrice: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  showRemoveBlock: { type: Boolean, default: false },
});

const emit = defineEmits([
  'switchPlan',
  'compareQuote',
  'create',
  'lockPrice',
  'unlockPrice',
  'toggleCollapse',
  'removeBlock',
]);

const { t } = useI18n();
const { currentAccount } = useAccount();
const planMenuOpen = ref(false);
const showClosedQuoteTabs = ref(false);
/** 'live' | 'quote' — which pricing table is shown when quotes exist */
const pricingView = ref('live');

const GRID_COLS = 'grid-cols-[minmax(0,1fr)_5rem_3.5rem_4.5rem_6rem]';

const PILL_BUTTON_CLASS =
  'inline-flex h-11 items-center gap-2 rounded-[22px] border px-4 text-sm font-semibold transition-colors border-slate-200 bg-white text-n-slate-12 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 disabled:opacity-50';

const currency = computed(
  () =>
    props.activeBreakdown?.currency ||
    props.compareQuote?.currency ||
    accountCurrency(currentAccount.value)
);

const formatAmount = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.value,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatHours = hours => {
  const n = Number(hours);
  if (!Number.isFinite(n)) return '';
  return `${Number(n.toFixed(2))}h`;
};

const hasQuotes = computed(() => props.quotes.length > 0);

const showLiveTab = computed(
  () => hasQuotes.value && !!props.activeBreakdown
);

const isLiveView = computed(
  () => showLiveTab.value && pricingView.value === 'live'
);

const isQuoteView = computed(
  () =>
    !!props.compareQuote &&
    (!showLiveTab.value || pricingView.value === 'quote')
);

const quoteOnlyMode = computed(
  () => !props.activeBreakdown && !!props.compareQuote
);

const showPricingTable = computed(
  () => !!props.activeBreakdown || hasQuotes.value
);

const showLockedBadge = computed(
  () => props.pricingLocked && !hasQuotes.value
);

const canShowPlanMenu = computed(
  () => !showLockedBadge.value && (!hasQuotes.value || !props.selectedPlan)
);

const planSelectorDisabled = computed(
  () =>
    props.plans.length === 0 ||
    props.readOnly ||
    (props.planLocked && !!props.selectedPlan)
);

const isClosedQuote = quote =>
  ['accepted', 'rejected', 'withdrawn', 'expired', 'superseded'].includes(
    quote?.status
  );

const closedQuotes = computed(() => props.quotes.filter(isClosedQuote));

const visibleQuoteTabs = computed(() => {
  if (showClosedQuoteTabs.value) return props.quotes;
  return props.quotes.filter(
    q => !isClosedQuote(q) || q.id === props.compareQuote?.id
  );
});

watch(
  () => [showLiveTab.value, props.compareQuote?.id],
  () => {
    if (!showLiveTab.value && props.compareQuote) {
      pricingView.value = 'quote';
    } else if (showLiveTab.value && !props.compareQuote) {
      pricingView.value = 'live';
    }
  },
  { immediate: true }
);

const statusLabel = status => {
  if (!status) return '';
  const key = `PLANNING.SHOW.QUOTE_STATUS_${String(status).toUpperCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const quoteTabTitle = quote =>
  quote.name || t('PLANNING.SHOW.QUOTE_DETAIL_TITLE');

const quoteTabLabel = quote =>
  t('PLANNING.SHOW.QUOTE_TAB_LABEL', {
    name: quoteTabTitle(quote),
    status: statusLabel(quote.status),
  });

const EM_DASH = '—';
const MINUS_SIGN = '−';

const suggestedPricing = computed(() => {
  const rateQuote =
    props.quotes.find(q => q.status === 'accepted') || props.compareQuote;
  return computeSuggestedPricing(props.activeBreakdown, rateQuote);
});

const livePricingRows = computed(() => suggestedPricing.value.rows);

const liveDiscount = computed(() => suggestedPricing.value.discount);

const liveTotal = computed(() => suggestedPricing.value.total);

const quoteDiscount = computed(() => {
  const breakdown = props.compareQuote?.breakdown;
  const fromField = Number(breakdown?.discount_amount || 0);
  if (fromField > 0) return fromField;
  return (breakdown?.line_items || [])
    .filter(l => l.is_discount)
    .reduce((sum, l) => sum + Math.abs(Number(l.line_total || 0)), 0);
});

const quoteDiscountLabel = computed(() => {
  const breakdown = props.compareQuote?.breakdown;
  const fromBreakdown = Array.isArray(breakdown?.discounts)
    ? breakdown.discounts
    : [];
  const fromOverrides = Array.isArray(props.compareQuote?.overrides?.discounts)
    ? props.compareQuote.overrides.discounts
    : [];
  let named = fromBreakdown;
  if (!named.length) named = fromOverrides;
  if (!named.length) {
    const single = breakdown?.discount || props.compareQuote?.overrides?.discount;
    named = single ? [single] : [];
  }
  const fromNamed = named
    .map(d => d?.label || d?.code || d?.name)
    .filter(Boolean);
  const names =
    fromNamed.length > 0
      ? fromNamed
      : (breakdown?.line_items || [])
          .filter(l => l.is_discount)
          .map(l => l.name)
          .filter(Boolean);
  if (!names.length) return t('PLANNING.SHOW.DOCUMENT_DISCOUNT');
  return t('PLANNING.SHOW.DISCOUNT_NAMED', { name: names.join(', ') });
});

const livePricingByKey = computed(() => {
  const map = new Map();
  livePricingRows.value.forEach(row => map.set(row.key, row.live));
  return map;
});

const lineRows = computed(() => {
  if (isLiveView.value) {
    return livePricingRows.value;
  }

  if (props.compareQuote && (isQuoteView.value || quoteOnlyMode.value)) {
    const quotedRows = (props.compareQuote.breakdown?.line_items || [])
      .filter(l => !l.is_discount)
      .map(l => ({
        key: pricingLineKey(l),
        name: l.name,
        // Suggested (live) calculation — full line struck through when quote overrules it
        live: livePricingByKey.value.get(pricingLineKey(l)) || null,
        quoted: l,
      }));
    const quotedKeys = new Set(quotedRows.map(row => row.key));
    const missingFromQuote = livePricingRows.value
      .filter(row => !quotedKeys.has(row.key))
      .map(row => ({
        key: row.key,
        name: row.name,
        live: row.live,
        quoted: null,
        suggestedOnly: true,
      }));
    return [...quotedRows, ...missingFromQuote];
  }

  if (props.activeBreakdown) {
    return (props.activeBreakdown.line_items || [])
      .filter(l => !l.is_discount)
      .map(l => ({
        key: pricingLineKey(l),
        name: l.name,
        live: l,
        quoted: null,
      }));
  }

  return [];
});

const lineGroups = computed(() =>
  groupPricingRows(lineRows.value, {
    eventWideLabel: t('PLANNING.SHOW.PRICING_EVENT_WIDE'),
    slotFallbackLabel: t('PLANNING.SHOW.PRICING_SLOT_FALLBACK'),
  })
);

const showSlotGroups = computed(() => lineGroups.value.length > 1);

const rowDisplayLine = row => {
  if (isLiveView.value) return row.live;
  if (isQuoteView.value || quoteOnlyMode.value) return row.quoted || row.live;
  return row.live || row.quoted;
};

const groupHasQuotedLines = group =>
  group.rows.some(row => !row.suggestedOnly);

const groupSubtotal = group =>
  group.rows.reduce((sum, row) => {
    if (row.suggestedOnly) return sum;
    const line = rowDisplayLine(row);
    return sum + Number(line?.line_total || 0);
  }, 0);

const formatLineQty = row => {
  const line = rowDisplayLine(row);
  if (!line || line.quantity == null) return EM_DASH;
  return String(line.quantity);
};

const formatLineUnit = row => {
  const line = rowDisplayLine(row);
  if (!line) return EM_DASH;
  return pricingUnitShortLabel(line.unit, t) || EM_DASH;
};

const personnelHint = row => {
  const line = rowDisplayLine(row);
  if (line?.personnel_count == null || line?.duration_hours == null)
    return null;
  const key =
    Number(line.personnel_count) === 1
      ? 'PLANNING.SHOW.PERSONNEL_LINE_ONE'
      : 'PLANNING.SHOW.PERSONNEL_LINE';
  return t(key, {
    count: line.personnel_count,
    duration: formatHours(line.duration_hours),
    rate: formatAmount(line.unit_price),
  });
};

const lineAdj = row => {
  const source = rowDisplayLine(row);
  const adj = source?.adjustment;
  if (!adj) return null;
  const value = Number(adj.value || 0);
  if (!value) return null;
  const amount = Number(source.adjustment_amount || 0);
  let label = t('PLANNING.SHOW.LINE_SURCHARGE');
  if (adj.type === 'percent') {
    label =
      value < 0
        ? t('PLANNING.SHOW.LINE_DISCOUNT_PERCENT', { value: Math.abs(value) })
        : t('PLANNING.SHOW.LINE_SURCHARGE_PERCENT', { value });
  } else if (value < 0) {
    label = t('PLANNING.SHOW.LINE_DISCOUNT');
  }
  return {
    label,
    amount,
    isSurcharge: amount > 0,
  };
};

const lineBaseTotal = line => {
  if (!line) return null;
  if (line.line_total != null && line.adjustment_amount == null) {
    return Number(line.line_total);
  }
  const qty = Number(line.quantity || 0);
  const rate = Number(line.unit_price || 0);
  return qty * rate;
};

const showOverruledLine = row => {
  if (!isQuoteView.value || !row.live || !row.quoted) return false;
  const live = row.live;
  const quoted = row.quoted;
  return (
    String(live.name || '') !== String(quoted.name || '') ||
    Number(live.unit_price) !== Number(quoted.unit_price) ||
    Number(live.quantity) !== Number(quoted.quantity) ||
    String(live.unit || '') !== String(quoted.unit || '') ||
    lineBaseTotal(live) !== lineBaseTotal(quoted)
  );
};

const formatLineQtyValue = line => {
  if (!line || line.quantity == null) return EM_DASH;
  return String(line.quantity);
};

const formatLineUnitValue = line => {
  if (!line) return EM_DASH;
  return pricingUnitShortLabel(line.unit, t) || EM_DASH;
};

const selectLiveTab = () => {
  pricingView.value = 'live';
};

const selectQuoteTab = quoteId => {
  pricingView.value = 'quote';
  emit('compareQuote', quoteId);
};

const displayDiscount = computed(() => {
  if (isLiveView.value) return liveDiscount.value;
  if (isQuoteView.value || quoteOnlyMode.value) return quoteDiscount.value;
  return 0;
});

const showDiscountRow = computed(
  () =>
    (isLiveView.value || isQuoteView.value || quoteOnlyMode.value) &&
    displayDiscount.value > 0
);

const menuActions = computed(() => {
  const actions = [];
  if (props.showRemoveBlock) {
    actions.push({
      key: 'remove-block',
      label: t('PLANNING.SHOW.REMOVE_BLOCK'),
      icon: 'i-lucide-trash-2',
      danger: true,
    });
  }
  if (!props.readOnly && (!props.quotesEnabled || props.pricingLocked)) {
    actions.push({
      key: props.pricingLocked ? 'unlock-price' : 'lock-price',
      label: props.pricingLocked
        ? t('PLANNING.SHOW.UNLOCK_PRICE')
        : t('PLANNING.SHOW.LOCK_PRICE'),
      icon: props.pricingLocked ? 'i-lucide-lock-open' : 'i-lucide-lock',
      disabled: props.lockingPrice,
    });
  }
  return actions;
});

const handleMenuAction = action => {
  if (action === 'remove-block') emit('removeBlock');
  if (action === 'unlock-price') emit('unlockPrice');
  if (action === 'lock-price') emit('lockPrice');
};
</script>

<template>
  <SectionCard
    :title="t('PLANNING.SHOW.PRICING_TITLE')"
    icon="i-lucide-receipt"
    collapsible
    collapse-in-menu
    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
    :expand-label="t('PLANNING.SHOW.EXPAND')"
    :menu-actions="menuActions"
    flush
    :collapsed="collapsed"
    @toggle-collapse="emit('toggleCollapse')"
    @menu-action="handleMenuAction"
  >
    <template v-if="canShowPlanMenu || showLockedBadge" #actions>
      <span
        v-if="showLockedBadge"
        class="inline-flex h-11 items-center gap-1.5 rounded-[22px] border border-amber-200 bg-amber-50 px-4 text-sm font-semibold text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/40 dark:!text-amber-700"
      >
        <Icon icon="i-lucide-lock" class="size-4" />
        {{ t('PLANNING.SHOW.PRICE_LOCKED') }}
      </span>
      <div
        v-else
        v-on-click-outside="() => (planMenuOpen = false)"
        class="relative"
      >
        <button
          type="button"
          :class="PILL_BUTTON_CLASS"
          :disabled="planSelectorDisabled"
          @click="planMenuOpen = !planMenuOpen"
        >
          <Icon
            icon="i-lucide-receipt"
            class="size-4 shrink-0 text-n-slate-10"
          />
          <span class="max-w-[14rem] truncate">{{
            selectedPlan?.name || t('PLANNING.SHOW.SELECT_PLAN')
          }}</span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-4 shrink-0 text-n-slate-10"
          />
        </button>
        <div
          v-if="planMenuOpen"
          class="absolute top-full right-0 z-50 mt-1 w-56 rounded-lg border border-n-weak bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-950"
        >
          <button
            v-for="plan in plans"
            :key="plan.id"
            type="button"
            class="w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-n-alpha-2"
            :class="{
              'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-300':
                plan.id === selectedPlanId,
            }"
            @click="
              emit('switchPlan', plan.id);
              planMenuOpen = false;
            "
          >
            {{ plan.name }}
          </button>
          <button
            v-if="!readOnly && quotesEnabled && !hasQuotes"
            type="button"
            class="mt-0.5 flex w-full items-center gap-2 rounded-md border-t border-n-slate-5 px-3 py-2 text-left text-sm font-medium text-woot-600 transition-colors hover:bg-woot-50 dark:border-slate-800 dark:text-woot-300 dark:hover:bg-woot-900/30"
            @click="
              emit('create');
              planMenuOpen = false;
            "
          >
            <Icon icon="i-lucide-plus" class="size-4" />
            {{ t('PLANNING.SHOW.NEW_QUOTE') }}
          </button>
        </div>
      </div>
    </template>

    <div
      v-if="!loadingPrice && !showPricingTable"
      class="py-8 text-center"
    >
      <Icon icon="i-lucide-receipt" class="mx-auto mb-2 size-12 text-woot-75" />
      <p class="text-sm font-semibold text-slate-500">
        {{ t('PLANNING.SHOW.NO_PRICING') }}
      </p>
    </div>

    <div
      v-else-if="loadingPrice && !showPricingTable"
      class="mt-4 overflow-hidden rounded-lg border border-n-weak bg-white dark:bg-slate-950"
      aria-busy="true"
      :aria-label="t('PLANNING.SHOW.PRICING_TITLE')"
    >
      <div class="px-3 pb-3 pt-2">
        <div
          class="grid gap-2 border-b border-n-slate-5 pb-2"
          :class="GRID_COLS"
        >
          <div class="h-3 w-16 animate-pulse rounded bg-n-slate-4" />
          <div class="ml-auto h-3 w-10 animate-pulse rounded bg-n-slate-4" />
          <div class="ml-auto h-3 w-8 animate-pulse rounded bg-n-slate-4" />
          <div class="ml-auto h-3 w-10 animate-pulse rounded bg-n-slate-4" />
          <div class="ml-auto h-3 w-12 animate-pulse rounded bg-n-slate-4" />
        </div>
        <div class="divide-y divide-n-slate-5">
          <div
            v-for="i in 4"
            :key="i"
            class="grid items-center gap-2 py-3"
            :class="GRID_COLS"
          >
            <div class="h-4 w-3/4 max-w-[12rem] animate-pulse rounded bg-n-slate-4" />
            <div class="ml-auto h-4 w-12 animate-pulse rounded bg-n-slate-4" />
            <div class="ml-auto h-4 w-8 animate-pulse rounded bg-n-slate-4" />
            <div class="ml-auto h-4 w-10 animate-pulse rounded bg-n-slate-4" />
            <div class="ml-auto h-4 w-14 animate-pulse rounded bg-n-slate-4" />
          </div>
        </div>
        <div class="space-y-2 border-t border-n-slate-5 pt-4">
          <div class="flex justify-between">
            <div class="h-5 w-16 animate-pulse rounded bg-n-slate-4" />
            <div class="h-5 w-20 animate-pulse rounded bg-n-slate-4" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="showPricingTable"
      class="mt-4 overflow-hidden rounded-lg border border-n-weak bg-white dark:bg-slate-950"
    >
      <div
        v-if="hasQuotes"
        class="flex items-stretch overflow-x-auto border-b border-n-weak bg-n-slate-3 dark:bg-slate-900"
      >
        <button
          v-if="showLiveTab"
          type="button"
          class="inline-flex !h-8 max-w-56 shrink-0 items-center gap-1.5 !rounded-none !border-t-0 !border-b-0 !border-l-0 border-r border-n-weak !px-2.5 !py-0 text-xs transition-colors"
          :class="
            isLiveView
              ? '-mb-px !border-b border-b-white bg-white font-semibold text-n-slate-12 dark:border-b-slate-950 dark:bg-slate-950'
              : 'bg-transparent font-normal text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-slate-800'
          "
          @click="selectLiveTab"
        >
          <Icon
            icon="i-lucide-activity"
            class="size-3.5 shrink-0 opacity-70"
          />
          <span class="truncate">{{ t('PLANNING.SHOW.LIVE_PRICING') }}</span>
        </button>
        <button
          v-for="q in visibleQuoteTabs"
          :key="q.id"
          type="button"
          class="inline-flex !h-8 max-w-56 shrink-0 items-center gap-1.5 !rounded-none !border-t-0 !border-b-0 !border-l-0 border-r border-n-weak !px-2.5 !py-0 text-xs transition-colors"
          :class="
            isQuoteView && compareQuote?.id === q.id
              ? '-mb-px !border-b border-b-white bg-white font-semibold text-n-slate-12 dark:border-b-slate-950 dark:bg-slate-950'
              : 'bg-transparent font-normal text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-slate-800'
          "
          @click="selectQuoteTab(q.id)"
        >
          <Icon
            icon="i-lucide-file-text"
            class="size-3.5 shrink-0 opacity-70"
          />
          <span class="truncate">{{ quoteTabLabel(q) }}</span>
        </button>
        <button
          v-if="closedQuotes.length"
          type="button"
          class="inline-flex !h-8 shrink-0 items-center justify-center !rounded-none !border-t-0 !border-b-0 !border-l-0 border-r border-n-weak !px-2 !py-0 text-xs transition-colors"
          :class="
            showClosedQuoteTabs
              ? '-mb-px !border-b border-b-white bg-white text-n-slate-12 dark:border-b-slate-950 dark:bg-slate-950'
              : 'bg-transparent text-n-slate-11 hover:bg-n-slate-4 dark:hover:bg-slate-800'
          "
          :title="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
          :aria-label="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
          :aria-expanded="showClosedQuoteTabs"
          @click="showClosedQuoteTabs = !showClosedQuoteTabs"
        >
          <Icon icon="i-lucide-ellipsis" class="size-4" />
        </button>
        <button
          v-if="!readOnly && quotesEnabled"
          type="button"
          class="inline-flex !h-8 shrink-0 items-center justify-center gap-1.5 !rounded-none !border-0 !px-2.5 !py-0 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-slate-4 hover:text-woot-600 dark:hover:bg-slate-800 dark:hover:text-woot-300"
          :title="t('PLANNING.SHOW.CREATE_QUOTE')"
          :aria-label="t('PLANNING.SHOW.CREATE_QUOTE')"
          @click="emit('create')"
        >
          <Icon icon="i-lucide-plus" class="size-4 shrink-0" />
          <span class="whitespace-nowrap">{{
            t('PLANNING.SHOW.CREATE_QUOTE')
          }}</span>
        </button>
      </div>

      <div class="px-3 pb-3 pt-2">
        <div
          class="grid gap-2 border-b border-n-slate-5 pb-2 text-xs font-semibold text-n-slate-12"
          :class="GRID_COLS"
        >
          <span>{{ t('PLANNING.SHOW.ITEM') }}</span>
          <span class="text-right">{{ t('PLANNING.SHOW.RATE') }}</span>
          <span class="text-right">{{ t('PLANNING.SHOW.QTY') }}</span>
          <span class="text-right">{{ t('PLANNING.SHOW.UNIT') }}</span>
          <span class="text-right">{{ t('PLANNING.SHOW.TOTAL') }}</span>
        </div>

        <div class="divide-y divide-n-slate-5">
          <div v-for="group in lineGroups" :key="group.key">
            <div
              v-if="showSlotGroups"
              class="grid gap-2 py-3"
              :class="[
                GRID_COLS,
                group.rows.every(row => row.suggestedOnly) ? 'opacity-50' : '',
              ]"
            >
              <div
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
              >
                {{ group.label }}
              </div>
              <div />
              <div />
              <div />
              <div
                class="text-right text-xs font-medium tabular-nums text-n-slate-10"
              >
                {{
                  groupHasQuotedLines(group)
                    ? formatAmount(groupSubtotal(group))
                    : EM_DASH
                }}
              </div>
            </div>

            <div
              v-for="(row, rowIndex) in group.rows"
              :key="`${group.key}-${row.key}-${rowIndex}`"
              class="py-3"
              :class="row.suggestedOnly ? 'opacity-50' : ''"
            >
              <div
                v-if="showOverruledLine(row)"
                class="mb-1 grid items-start gap-2 text-n-slate-9 line-through"
                :class="GRID_COLS"
              >
                <div class="min-w-0 text-sm">
                  {{ row.live.name }}
                </div>
                <div class="text-right text-xs tabular-nums">
                  {{ formatAmount(row.live.unit_price) }}
                </div>
                <div class="text-right text-xs tabular-nums">
                  {{ formatLineQtyValue(row.live) }}
                </div>
                <div class="text-right text-xs">
                  {{ formatLineUnitValue(row.live) }}
                </div>
                <div class="text-right text-xs tabular-nums">
                  {{ formatAmount(lineBaseTotal(row.live)) }}
                </div>
              </div>
              <div class="grid items-start gap-2" :class="GRID_COLS">
                <div class="min-w-0">
                  <div class="text-sm font-medium text-n-slate-12">
                    <span>{{ rowDisplayLine(row)?.name || row.name }}</span>
                    <span
                      v-if="row.suggestedOnly"
                      class="ml-1 text-xs font-normal text-n-slate-11"
                    >
                      {{ t('PLANNING.SHOW.ADDED_AFTER_QUOTING') }}
                    </span>
                  </div>
                  <div
                    v-if="personnelHint(row)"
                    class="mt-0.5 text-xs text-n-slate-11"
                  >
                    {{ personnelHint(row) }}
                  </div>
                </div>
                <div class="text-right text-sm tabular-nums text-n-slate-11">
                  {{ formatAmount(rowDisplayLine(row)?.unit_price) }}
                </div>
                <div
                  class="text-right text-sm font-semibold tabular-nums text-n-slate-12"
                >
                  {{ formatLineQty(row) }}
                </div>
                <div class="text-right text-sm text-n-slate-11">
                  {{ formatLineUnit(row) }}
                </div>
                <div
                  class="text-right text-sm font-semibold tabular-nums text-n-slate-12"
                >
                  {{
                    row.suggestedOnly
                      ? EM_DASH
                      : formatAmount(lineBaseTotal(rowDisplayLine(row)))
                  }}
                </div>
              </div>
              <div
                v-if="lineAdj(row)"
                class="mt-1 grid gap-2"
                :class="GRID_COLS"
              >
                <div
                  class="text-xs font-medium"
                  :class="
                    lineAdj(row).isSurcharge
                      ? 'text-red-600'
                      : 'text-emerald-700'
                  "
                >
                  {{ lineAdj(row).label }}
                </div>
                <div />
                <div />
                <div />
                <div
                  class="text-right text-xs font-semibold tabular-nums"
                  :class="
                    lineAdj(row).isSurcharge
                      ? 'text-red-600'
                      : 'text-emerald-700'
                  "
                >
                  {{
                    `${lineAdj(row).amount > 0 ? '+' : MINUS_SIGN}${formatAmount(
                      Math.abs(lineAdj(row).amount)
                    )}`
                  }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="showDiscountRow"
            class="grid gap-2 py-3"
            :class="GRID_COLS"
          >
            <div>
              <div class="text-sm font-medium text-n-slate-12">
                {{ quoteDiscountLabel }}
              </div>
            </div>
            <div class="text-right text-n-slate-9">{{ EM_DASH }}</div>
            <div class="text-right text-n-slate-9">{{ EM_DASH }}</div>
            <div class="text-right text-n-slate-9">{{ EM_DASH }}</div>
            <div
              class="text-right text-sm font-semibold tabular-nums text-n-slate-11"
            >
              {{ MINUS_SIGN }}{{ formatAmount(displayDiscount) }}
            </div>
          </div>
        </div>

        <div class="space-y-2 pt-4">
          <template v-if="isLiveView">
            <div
              class="flex justify-between text-base font-semibold text-woot-600 dark:text-woot-300"
            >
              <span>{{ t('PLANNING.SHOW.TOTAL') }}</span>
              <span class="tabular-nums">{{ formatAmount(liveTotal) }}</span>
            </div>
          </template>
          <template v-else-if="isQuoteView || quoteOnlyMode">
            <div
              class="flex justify-between text-base font-semibold text-woot-600 dark:text-woot-300"
            >
              <span>{{ t('PLANNING.SHOW.QUOTED_TOTAL') }}</span>
              <span class="tabular-nums">{{
                formatAmount(compareQuote.total_excl_tax)
              }}</span>
            </div>
          </template>
          <template v-else>
            <div
              class="flex justify-between text-base font-semibold text-woot-600 dark:text-woot-300"
            >
              <span>{{ t('PLANNING.SHOW.TOTAL') }}</span>
              <span class="tabular-nums">{{ formattedTotal }}</span>
            </div>
            <div
              v-if="!quotesEnabled && pricingLocked && formattedDelta"
              class="flex justify-between text-sm text-n-slate-11"
            >
              <span>{{ t('PLANNING.SHOW.PRICE_DELTA') }}</span>
              <span
                class="font-semibold tabular-nums"
                :class="priceDelta > 0 ? 'text-red-500' : 'text-emerald-500'"
              >
                {{ formattedDelta }}
              </span>
            </div>
          </template>
          <p v-if="selectedPlan?.id" class="pt-2 text-xs text-n-slate-11">
            {{ t('PLANNING.SHOW.PRICED_ACCORDING_TO_PLAN_PREFIX') }}
            <router-link
              :to="{
                name: 'settings_pricing_plan_show',
                params: { planId: selectedPlan.id },
              }"
              class="text-xs font-normal text-inherit underline hover:text-woot-600"
            >
              {{ selectedPlan.name }}
            </router-link>
            {{ t('PLANNING.SHOW.PRICED_ACCORDING_TO_PLAN_SUFFIX') }}
          </p>
        </div>
      </div>
    </div>
  </SectionCard>
</template>
